/**
 * Grabación de consulta en cada episodio (26/9/2026) — la parte que
 * no es pantalla: el formato, el micrófono, la cola que sube el audio a trozos
 * con reintento y la sesión de grabación entera (empezar, pausa, seguir,
 * terminar). La pantalla está en components/patient/GrabacionConsulta.jsx y el
 * servidor en backend/routers/grabaciones_router.py.
 *
 * QUE NO SE PIERDA NADA. MediaRecorder entrega el audio cada `trozoMs` (10 s)
 * y cada trozo se sube en cuanto sale, con su número de orden, UNO DETRÁS DE
 * OTRO: si la red falla, ese trozo se reintenta (1 s, 2 s, 4 s… hasta 30 s,
 * sin rendirse mientras la pestaña siga abierta) y los siguientes esperan en
 * memoria. Así, si se cierra la pestaña, lo que no llegó es solo el final; lo
 * subido queda en el servidor como grabación «incompleta» y se puede oír.
 * El servidor es idempotente por número de trozo: un reintento de algo que sí
 * llegó no se guarda dos veces.
 */
import { useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { api } from './api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Lo que se puede ajustar (y lo que los tests acortan). */
export const CONFIG = {
  trozoMs: 10000,
  latidoMs: 30000,
  relojMs: 500,
  reintentosMs: [1000, 2000, 4000, 8000, 15000, 30000],
  // Voz: 32 kbps son unos 7 MB por media hora.
  tasaBits: 32000,
  topeSegundos: 2 * 3600,
  avisoAntesSegundos: 10 * 60,
  topeBytes: 60 * 1024 * 1024,
  ahora: () => Date.now(),
};

// ---------------------------------------------------------------------------
// Llamadas al servidor
// ---------------------------------------------------------------------------
export async function listarGrabacionesDelPaciente(patientId) {
  const { data } = await api.get('/api/grabaciones', { params: { patient_id: patientId } });
  return Array.isArray(data) ? data : [];
}

export async function crearGrabacion({ episodio_id: episodioId, mime }) {
  const { data } = await api.post('/api/grabaciones', { episodio_id: episodioId, mime });
  return data;
}

export async function subirTrozo(grabacionId, n, blob, tMs) {
  const { data } = await api.put(`/api/grabaciones/${grabacionId}/trozos/${n}`, blob, {
    params: { t_ms: Math.max(0, Math.round(tMs || 0)) },
    headers: { 'Content-Type': 'application/octet-stream' },
    timeout: 60000,
  });
  return data;
}

export async function latidoGrabacion(grabacionId) {
  const { data } = await api.post(`/api/grabaciones/${grabacionId}/latido`);
  return data;
}

export async function terminarGrabacion(grabacionId, { trozos, duracionMs }) {
  const { data } = await api.post(`/api/grabaciones/${grabacionId}/terminar`, {
    trozos, duracion_ms: Math.max(0, Math.round(duracionMs || 0)),
  });
  return data;
}

export async function borrarGrabacion(grabacionId) {
  const { data } = await api.delete(`/api/grabaciones/${grabacionId}`);
  return data;
}

/** El audio se pide al servidor con la sesión (cookie) y con `Range`: el
 *  reproductor puede saltar a cualquier minuto. */
export function urlAudio(grabacionId) {
  return `${BACKEND_URL}/api/grabaciones/${grabacionId}/audio`;
}

// ---------------------------------------------------------------------------
// Formato y micrófono
// ---------------------------------------------------------------------------
const FORMATOS = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4', 'audio/ogg;codecs=opus'];
const FORMATOS_SAFARI = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/webm;codecs=opus',
  'audio/webm'];

/** Safari (y todo lo que corre en un iPad o iPhone, que es WebKit por dentro). */
export function esSafari(ua) {
  const u = ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');
  if (/CriOS|FxiOS|EdgiOS/.test(u)) return true;
  return /Safari/.test(u) && !/Chrome|Chromium|Edg|Firefox|Android/.test(u);
}

/** El formato de grabación: WebM/Opus en Chrome; MP4 en Safari (iPad). */
export function elegirFormato(Grabador, ua) {
  if (!Grabador || typeof Grabador.isTypeSupported !== 'function') return '';
  const lista = esSafari(ua) ? FORMATOS_SAFARI : FORMATOS;
  return lista.find((t) => {
    try { return Grabador.isTypeSupported(t); } catch (e) { return false; }
  }) || '';
}

/** ¿Este navegador puede grabar aquí? `{ ok, motivo }` con qué hacer si no. */
export function sePuedeGrabar() {
  if (typeof window === 'undefined') return { ok: false, motivo: 'Sin navegador.' };
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location?.hostname || '');
  if (window.isSecureContext === false && !local) {
    return { ok: false, motivo: 'La grabación necesita la conexión segura (https). Abre ClinicPilot desde su dirección con https.' };
  }
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function'
      || typeof window.MediaRecorder !== 'function') {
    return {
      ok: false,
      motivo: 'Este navegador no puede grabar audio. Usa Chrome o Safari actualizados (en iPad, Safari con iPadOS al día).',
    };
  }
  try {
    const politica = document.permissionsPolicy || document.featurePolicy;
    if (politica && typeof politica.allowsFeature === 'function' && !politica.allowsFeature('microphone')) {
      return {
        ok: false,
        motivo: 'La web tiene el micrófono bloqueado por la configuración del servidor (cabecera Permissions-Policy). Avisa para que lo cambien: el navegador no deja grabar hasta entonces.',
      };
    }
  } catch (e) { /* navegador sin la API: se sabrá al pedir el micrófono */ }
  return { ok: true, motivo: '' };
}

/** Qué decirle a la persona cuando el micrófono no se deja usar. */
export function mensajeMicrofono(err) {
  const nombre = err?.name || '';
  if (nombre === 'NotAllowedError' || nombre === 'SecurityError' || nombre === 'PermissionDeniedError') {
    return 'El navegador no deja usar el micrófono. Pulsa el candado de la barra de direcciones, pon «Micrófono: Permitir» y vuelve a pulsar «Grabar consulta». En iPad: Ajustes > Safari > Micrófono > Permitir.';
  }
  if (nombre === 'NotFoundError' || nombre === 'OverconstrainedError' || nombre === 'DevicesNotFoundError') {
    return 'No se encuentra ningún micrófono. Conecta uno (o revisa que no esté desactivado) y vuelve a intentarlo.';
  }
  if (nombre === 'NotReadableError' || nombre === 'TrackStartError' || nombre === 'AbortError') {
    return 'El micrófono está ocupado por otra aplicación (¿una videollamada?). Ciérrala y vuelve a intentarlo.';
  }
  return `No se ha podido usar el micrófono${err?.message ? ` (${err.message})` : ''}. Vuelve a intentarlo o prueba en Chrome o Safari.`;
}

/** 75 → «01:15»; 3725 → «1:02:05». */
export function formatoTiempo(segundos) {
  const s = Math.max(0, Math.floor(Number(segundos) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${String(m).padStart(2, '0')}:${ss}`;
}

// ---------------------------------------------------------------------------
// La cola de trozos
// ---------------------------------------------------------------------------
/** Un fallo que no se arregla reintentando: la grabación ya no está (404),
 *  ya se terminó (409), se ha llegado al tope (413) o la petición es mala.
 *  La red caída, un 5xx o la sesión caducada (401) SÍ se reintentan. */
export function esFatal(status) {
  return !!status && status >= 400 && status < 500 && ![401, 408, 429].includes(status);
}

/** Los dos 413 no son lo mismo (segunda pasada, 26/9/2026): el TOPE de la
 *  grabación (`codigo: 'tope'`) la termina; un trozo DEMASIADO GRANDE (el
 *  del servidor, `codigo: 'trozo_grande'`, o el de un proxy, que no trae
 *  código) solo pierde ese trozo: se sigue con los demás. */
export function esTope(e) {
  return e?.response?.status === 413 && e?.response?.data?.codigo === 'tope';
}

export function esTrozoGrande(e) {
  return e?.response?.status === 413 && !esTope(e);
}

const esperarMs = (ms) => new Promise((r) => { setTimeout(r, ms); });

export class ColaDeTrozos {
  constructor({ enviar, alCambiar = () => {}, esperar = esperarMs, reintentosMs } = {}) {
    this.enviar = enviar;
    this.alCambiar = alCambiar;
    this.esperar = esperar;
    this.reintentosMs = reintentosMs;
    this.cola = [];
    this.andando = false;
    this.parada = false;
    this.subidos = 0;
    this.bytes = 0;
    this.intentos = 0;
    this.error = null;
    this.saltados = [];
    this.esperando = [];
  }

  get estado() {
    return {
      pendientes: this.cola.length,
      subidos: this.subidos,
      saltados: this.saltados.slice(),
      bytes: this.bytes,
      reintentando: this.intentos > 0,
      intentos: this.intentos,
      error: this.error,
    };
  }

  poner(n, blob, tMs) {
    if (this.parada) return;
    this.cola.push({ n, blob, tMs });
    this.alCambiar(this.estado);
    this.bucle();
  }

  /** Se resuelve cuando todo lo puesto ha llegado; falla si hubo un error
   *  que no se arregla reintentando. */
  vaciada() {
    if (this.error) return Promise.reject(this.error);
    if (!this.andando && !this.cola.length) return Promise.resolve();
    return new Promise((resolve, reject) => { this.esperando.push({ resolve, reject }); });
  }

  parar() {
    this.parada = true;
  }

  avisarA() {
    const quienes = this.esperando.splice(0);
    quienes.forEach(({ resolve, reject }) => (this.error ? reject(this.error) : resolve()));
  }

  async bucle() {
    if (this.andando) return;
    this.andando = true;
    while (this.cola.length && !this.parada) {
      const t = this.cola[0];
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await this.enviar(t.n, t.blob, t.tMs);
        this.cola.shift();
        this.subidos += 1;
        this.bytes = typeof r?.bytes === 'number' ? r.bytes : this.bytes + (t.blob?.size || 0);
        this.intentos = 0;
        this.alCambiar(this.estado);
      } catch (e) {
        if (esTrozoGrande(e)) {
          // Ese trozo no cabe: se pierde (la grabación quedará incompleta) y
          // se sigue con los demás, que sí caben.
          this.cola.shift();
          this.saltados.push(t.n);
          this.intentos = 0;
          this.alCambiar(this.estado);
          // eslint-disable-next-line no-continue
          continue;
        }
        if (esFatal(e?.response?.status)) {
          this.error = e;
          this.parada = true;
          this.alCambiar(this.estado);
          break;
        }
        const lista = this.reintentosMs || CONFIG.reintentosMs;
        const espera = lista[Math.min(this.intentos, lista.length - 1)];
        this.intentos += 1;
        this.alCambiar(this.estado);
        // eslint-disable-next-line no-await-in-loop
        await this.esperar(espera);
      }
    }
    this.andando = false;
    this.alCambiar(this.estado);
    if (this.error || !this.cola.length) this.avisarA();
  }
}

// ---------------------------------------------------------------------------
// La sesión de grabación
// ---------------------------------------------------------------------------
function pararPistas(stream) {
  try { (stream?.getTracks?.() || []).forEach((p) => { try { p.stop(); } catch (e) { /* ya parada */ } }); } catch (e) { /* nada */ }
}

const detalle = (e, porDefecto) => e?.response?.data?.detail || porDefecto;

/** «2 horas» (o lo que diga CONFIG). */
function textoTope() {
  const s = CONFIG.topeSegundos;
  if (s % 3600 === 0) return `${s / 3600} hora${s === 3600 ? '' : 's'}`;
  const m = Math.round(s / 60);
  return `${m} minuto${m === 1 ? '' : 's'}`;
}

/**
 * Una grabación de principio a fin. La pantalla solo pinta `estado`:
 *   fase: 'pidiendo' → 'grabando' ⇄ 'pausada' → 'guardando' → 'hecha' | 'fallida'
 * y llama a pausar / seguir / terminar. Vive fuera de React (la lleva
 * `gestorGrabacion`, más abajo): cambiar de pestaña en la ficha o de pantalla
 * en la app NO la para.
 */
export class SesionDeGrabacion {
  constructor({
    episodioId, patientId = null, pacienteNombre = '', alCambiar = () => {}, alAcabar = () => {},
  }) {
    this.episodioId = episodioId;
    this.patientId = patientId;
    this.pacienteNombre = pacienteNombre;
    this.alCambiar = alCambiar;
    this.alAcabar = alAcabar;
    this.id = null;
    this.n = 0;
    this.acumuladoMs = 0;
    this.desde = 0;
    this.pausada = false;
    this.terminando = false;
    this.porTope = false;
    this.avisadoTiempo = false;
    this.avisadoBytes = false;
    this.estado = {
      fase: 'pidiendo', segundos: 0, pendientes: 0, reintentando: false, intentos: 0,
      bytes: 0, error: '', aviso: '',
    };
  }

  cambiar(parte) {
    this.estado = { ...this.estado, ...parte };
    try { this.alCambiar(this.estado); } catch (e) { /* la pantalla ya no está */ }
  }

  tiempoMs() {
    return this.acumuladoMs + (this.pausada || !this.desde ? 0 : CONFIG.ahora() - this.desde);
  }

  /** Pide el micrófono, abre la grabación en el servidor y empieza. Devuelve
   *  false (con `estado.error` explicando qué hacer) si no se puede. */
  async empezar() {
    const soporte = sePuedeGrabar();
    if (!soporte.ok) return this.fallar(soporte.motivo);
    const mime = elegirFormato(window.MediaRecorder);
    if (!mime) {
      return this.fallar('Este navegador no graba audio en ningún formato que se pueda guardar. Usa Chrome o Safari actualizados.');
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: false, noiseSuppression: true, autoGainControl: true },
      });
    } catch (e) {
      return this.fallar(mensajeMicrofono(e));
    }
    if (this.abandonada) {
      pararPistas(this.stream);
      return this.fallar('');
    }
    try {
      this.recorder = new window.MediaRecorder(this.stream, { mimeType: mime, audioBitsPerSecond: CONFIG.tasaBits });
    } catch (e) {
      try { this.recorder = new window.MediaRecorder(this.stream); } catch (e2) {
        pararPistas(this.stream);
        return this.fallar(`No se ha podido empezar a grabar: ${e2?.message || e?.message || 'error del navegador'}.`);
      }
    }
    let creada;
    try {
      creada = await crearGrabacion({ episodio_id: this.episodioId, mime: this.recorder.mimeType || mime });
    } catch (e) {
      pararPistas(this.stream);
      return this.fallar(detalle(e, 'No se ha podido empezar la grabación en el servidor. Vuelve a intentarlo.'));
    }
    this.id = creada.id;
    if (this.abandonada) {
      // Se salió de la ficha mientras se pedía el micrófono: nada grabado, y
      // el servidor quita la grabación vacía al terminarla con 0 trozos.
      pararPistas(this.stream);
      terminarGrabacion(this.id, { trozos: 0, duracionMs: 0 }).catch(() => {});
      return this.fallar('');
    }
    this.cola = new ColaDeTrozos({
      enviar: (n, blob, tMs) => subirTrozo(this.id, n, blob, tMs),
      alCambiar: (c) => this.alCambiarLaCola(c),
    });
    this.recorder.ondataavailable = (ev) => {
      if (ev?.data && ev.data.size > 0) {
        const n = this.n;
        this.n += 1;
        this.cola.poner(n, ev.data, this.tiempoMs());
      }
    };
    this.recorder.onstop = () => { this.cerrar(); };
    this.recorder.onerror = (ev) => {
      this.cambiar({ error: `El navegador ha cortado la grabación (${ev?.error?.name || 'error'}). Lo grabado hasta aquí se guarda.` });
      this.terminar();
    };
    this.desde = CONFIG.ahora();
    this.recorder.start(CONFIG.trozoMs);
    this.reloj = setInterval(() => this.tic(), CONFIG.relojMs);
    this.latido = setInterval(() => { latidoGrabacion(this.id).catch(() => {}); }, CONFIG.latidoMs);
    this.cambiar({ fase: 'grabando', segundos: 0, error: '' });
    return true;
  }

  fallar(motivo) {
    this.cambiar({ fase: 'fallida', error: motivo });
    this.alAcabar(this);
    return false;
  }

  alCambiarLaCola(c) {
    this.cambiar({
      pendientes: c.pendientes, reintentando: c.reintentando, intentos: c.intentos, bytes: c.bytes,
    });
    if (c.saltados.length && c.saltados.length !== this.saltadosVistos) {
      this.saltadosVistos = c.saltados.length;
      this.cambiar({
        aviso: `${c.saltados.length === 1 ? 'Un trozo' : `${c.saltados.length} trozos`} de audio `
          + 'ha sido demasiado grande para guardarlo: la grabación quedará incompleta (se sigue grabando).',
      });
    }
    if (!this.avisadoBytes && c.bytes >= CONFIG.topeBytes * 0.9) {
      this.avisadoBytes = true;
      this.cambiar({ aviso: 'La grabación está cerca de su tope de tamaño: termínala pronto (lo grabado se guarda).' });
    }
    if (c.error && !this.errorVisto) {
      this.errorVisto = true;
      const status = c.error?.response?.status;
      if (esTope(c.error)) {
        this.porTope = true;
        this.cambiar({ aviso: detalle(c.error, 'Se ha llegado al tope de la grabación: lo grabado hasta aquí está guardado.') });
      } else if (status === 404) {
        this.cambiar({ error: 'La grabación ya no está en el servidor (se ha borrado, o el episodio o la ficha ya no existen). Se ha parado.' });
      } else {
        this.cambiar({ error: detalle(c.error, 'No se ha podido guardar el audio.') });
      }
      this.terminar();
    }
  }

  tic() {
    const segundos = Math.floor(this.tiempoMs() / 1000);
    if (segundos !== this.estado.segundos) this.cambiar({ segundos });
    const aviso = CONFIG.topeSegundos - CONFIG.avisoAntesSegundos;
    if (!this.avisadoTiempo && segundos >= aviso && segundos < CONFIG.topeSegundos) {
      this.avisadoTiempo = true;
      const min = Math.round(CONFIG.avisoAntesSegundos / 60);
      this.cambiar({ aviso: `Quedan ${min} minuto${min === 1 ? '' : 's'} para el tope de ${textoTope()}: al llegar, la grabación se termina y se guarda sola.` });
    }
    if (segundos >= CONFIG.topeSegundos && !this.terminando) {
      this.porTope = true;
      this.cambiar({ aviso: `Se ha llegado al tope de ${textoTope()}: la grabación se ha terminado y está guardada.` });
      this.terminar();
    }
  }

  pausar() {
    if (this.estado.fase !== 'grabando' || !this.recorder) return;
    try { this.recorder.pause(); } catch (e) { return; }
    this.acumuladoMs = this.tiempoMs();
    this.pausada = true;
    this.cambiar({ fase: 'pausada' });
  }

  seguir() {
    if (this.estado.fase !== 'pausada' || !this.recorder) return;
    try { this.recorder.resume(); } catch (e) { return; }
    this.desde = CONFIG.ahora();
    this.pausada = false;
    this.cambiar({ fase: 'grabando' });
  }

  /** Salir de la ficha: si aún se estaba pidiendo el micrófono, no se
   *  empieza; si ya se grababa, se termina y se guarda lo grabado. */
  abandonar() {
    if (this.estado.fase === 'pidiendo') {
      this.abandonada = true;
      return;
    }
    this.terminar();
  }

  /** Para el micrófono y guarda: el último trozo sale al parar (onstop). */
  terminar() {
    if (this.terminando || !this.recorder || !this.cola) return;
    this.duracionMs = this.tiempoMs();
    this.acumuladoMs = this.duracionMs;
    this.pausada = true;
    this.cambiar({ fase: 'guardando' });
    let parado = false;
    try {
      if (this.recorder.state !== 'inactive') {
        this.recorder.stop();
        parado = true;
      }
    } catch (e) { /* ya estaba parado */ }
    if (!parado) this.cerrar();
  }

  /** Tras el último trozo: esperar a que todo haya subido y cerrar. */
  async cerrar() {
    if (this.cerrando) return;
    this.cerrando = true;
    this.terminando = true;
    clearInterval(this.reloj);
    clearInterval(this.latido);
    pararPistas(this.stream);
    if (this.estado.fase !== 'guardando') {
      if (this.duracionMs == null) this.duracionMs = this.tiempoMs();
      this.cambiar({ fase: 'guardando' });
    }
    let trozos = this.n;
    try {
      await this.cola.vaciada();
    } catch (e) {
      if (!this.porTope) {
        this.cambiar({ fase: 'fallida' });
        this.alAcabar(this);
        return;
      }
      // Tope: lo que llegó va seguido (la cola sube en orden, y lo saltado
      // por demasiado grande queda como hueco).
      trozos = this.cola.subidos + this.cola.saltados.length;
    }
    // NUNCA «guardada» sin audio (segunda pasada, 26/9/2026).
    const sinAudio = (motivo) => {
      this.cambiar({ fase: 'fallida', error: `No se ha guardado audio: ${motivo}` });
      this.alAcabar(this);
    };
    const porQue = () => {
      if (this.cola.saltados.length) return 'los trozos que ha entregado el navegador eran demasiado grandes para guardarlos.';
      if (this.porTope) return 'se había llegado al tope de la grabación.';
      return 'no ha llegado ningún trozo de sonido al servidor.';
    };
    if (!this.cola.subidos) {
      // El servidor quita la grabación vacía al terminarla sin trozos.
      terminarGrabacion(this.id, { trozos: 0, duracionMs: 0 }).catch(() => {});
      sinAudio(porQue());
      return;
    }
    let respuesta;
    try {
      respuesta = await terminarGrabacion(this.id, { trozos, duracionMs: this.duracionMs });
    } catch (e) {
      this.cambiar({
        fase: 'hecha',
        error: detalle(e, 'El audio está guardado, pero no se ha podido cerrar la grabación: saldrá como incompleta y se puede escuchar igual.'),
      });
      this.alAcabar(this);
      return;
    }
    if (respuesta?.vacia) {
      sinAudio(porQue());
      return;
    }
    this.incompleta = respuesta?.estado === 'incompleta' || this.cola.saltados.length > 0;
    this.cambiar({ fase: 'hecha' });
    this.alAcabar(this);
  }
}

// ---------------------------------------------------------------------------
// El gestor: UNA grabación en marcha en toda la app (segunda pasada, 26/9/2026)
// ---------------------------------------------------------------------------
// Antes la sesión vivía dentro de la tarjeta del episodio, y cambiar de
// pestaña en la ficha (Recetas, Consentimientos…), cerrar el episodio o
// imprimir la visita de hoy la desmontaba y TERMINABA la grabación: el médico
// hacía una receta en mitad de la consulta y dejaba de grabarse. Ahora vive
// aquí, a nivel de módulo, y no se desmonta al navegar por la app. Se ve en
// la barra flotante (components/BarraGrabacion.jsx, en AppShell) y en la
// tarjeta de su episodio. Solo se termina con «Terminar», al cerrar sesión
// (preguntando antes) o al cerrar la pestaña (el navegador pregunta).
const EN_MARCHA_GESTOR = new Set(['pidiendo', 'grabando', 'pausada', 'guardando']);
const oyentes = new Set();
let actual = null;
let ultima = null;
let terminadas = 0;
let foco = null;
let resolverFin = null;
let finPendiente = Promise.resolve();
let foto = { sesion: null, estado: null, ultima: null, terminadas: 0 };

function avisarOyentes() {
  foto = { sesion: actual, estado: actual ? actual.estado : null, ultima, terminadas };
  oyentes.forEach((fn) => { try { fn(); } catch (e) { /* un oyente roto no para a los demás */ } });
}

const AVISO_SALIDA = 'Hay una grabación de consulta en marcha. Si sales ahora, se pierde lo que falta por subir.';

function alSalirDeLaPagina(e) {
  e.preventDefault();
  e.returnValue = AVISO_SALIDA;
  return AVISO_SALIDA;
}

function alAcabarSesion(fin) {
  if (actual !== fin) return;
  actual = null;
  window.removeEventListener('beforeunload', alSalirDeLaPagina);
  const { fase, error, aviso } = fin.estado;
  ultima = {
    id: fin.id, episodioId: fin.episodioId, patientId: fin.patientId, fase, error, aviso,
  };
  if (fin.id) terminadas += 1;
  if (fase === 'hecha' && !error) {
    toast.success(fin.incompleta
      ? 'Grabación de la consulta guardada, pero incompleta: falta algún trozo.'
      : 'Grabación de la consulta guardada');
  } else if (fin.id && error) {
    // Ya había empezado: quien graba puede estar en otra pantalla.
    toast.error(error);
  }
  const r = resolverFin;
  resolverFin = null;
  avisarOyentes();
  if (r) r();
}

export const gestorGrabacion = {
  suscribir(fn) {
    oyentes.add(fn);
    return () => { oyentes.delete(fn); };
  },
  foto() {
    return foto;
  },
  get sesion() {
    return actual;
  },
  enMarcha() {
    return !!actual && EN_MARCHA_GESTOR.has(actual.estado.fase);
  },
  /** Empieza a grabar en un episodio. false (con el motivo en `ultima`) si no
   *  se ha podido, o si ya hay otra grabación en marcha. */
  async empezar({ episodioId, patientId, pacienteNombre }) {
    if (actual) return false;
    const s = new SesionDeGrabacion({
      episodioId,
      patientId,
      pacienteNombre,
      alCambiar: () => { if (actual === s) avisarOyentes(); },
      alAcabar: alAcabarSesion,
    });
    actual = s;
    finPendiente = new Promise((r) => { resolverFin = r; });
    window.addEventListener('beforeunload', alSalirDeLaPagina);
    avisarOyentes();
    return s.empezar();
  },
  pausar() { if (actual) actual.pausar(); },
  seguir() { if (actual) actual.seguir(); },
  terminar() { if (actual) actual.abandonar(); },
  /** La barra flotante: volver a la ficha del paciente, a su episodio. */
  irA(navigate) {
    if (!actual) return;
    foco = actual.episodioId;
    if (actual.patientId) navigate(`/pacientes/${actual.patientId}`);
  },
  /** La tarjeta del episodio pregunta al montarse si tiene que ponerse a la
   *  vista (se ha llegado desde la barra). */
  tomarFoco(episodioId) {
    if (foco && foco === episodioId) {
      foco = null;
      return true;
    }
    return false;
  },
  /** Cerrar sesión con una grabación en marcha: se pregunta, y si se sigue,
   *  se termina y se espera a que suba lo grabado (con la sesión aún viva). */
  async antesDeCerrarSesion({ confirmar = (t) => window.confirm(t), esperaMs = 20000 } = {}) {
    if (!actual) return true;
    const quien = actual.pacienteNombre ? ` (${actual.pacienteNombre})` : '';
    if (!confirmar(`Hay una grabación de consulta en marcha${quien}.\n\nSi cierras sesión, `
      + 'se termina y se guarda lo grabado. ¿Cerrar sesión?')) return false;
    const fin = finPendiente;
    actual.abandonar();
    await Promise.race([fin, new Promise((r) => { setTimeout(r, esperaMs); })]);
    return true;
  },
  /** Solo para las pruebas: vuelve al estado inicial. */
  olvidarParaPruebas() {
    actual = null;
    ultima = null;
    terminadas = 0;
    foco = null;
    resolverFin = null;
    window.removeEventListener('beforeunload', alSalirDeLaPagina);
    avisarOyentes();
  },
};

/** La grabación en marcha (o null), su estado y la última que terminó. Se
 *  repinta a cada tic del cronómetro: para la barra flotante. */
export function useGrabacionEnCurso() {
  return useSyncExternalStore(gestorGrabacion.suscribir, gestorGrabacion.foto, gestorGrabacion.foto);
}

/** Lo que le importa a la tarjeta de UN episodio, sin repintarla a cada tic
 *  si la grabación en marcha es de otro:
 *   · `estado`: el de la grabación de este episodio, si está en marcha;
 *   · `otra`: el paciente de la que está en marcha en otro sitio;
 *   · `ultima`: cómo acabó la última de este episodio (errores y avisos). */
export function useGrabacionDelEpisodio(episodioId) {
  const leer = () => {
    if (actual) return actual.episodioId === episodioId ? foto : `otra:${actual.pacienteNombre || ''}`;
    return ultima && ultima.episodioId === episodioId ? ultima : null;
  };
  const snap = useSyncExternalStore(gestorGrabacion.suscribir, leer, leer);
  if (snap && snap === foto && foto.sesion) return { estado: foto.estado, otra: null, ultima: null };
  if (typeof snap === 'string') return { estado: null, otra: snap.slice(5) || 'otro paciente', ultima: null };
  return { estado: null, otra: null, ultima: snap };
}

/** Cuántas grabaciones han terminado en esta sesión de la app: la historia
 *  recarga su lista cuando cambia, aunque la tarjeta se haya remontado. */
export function useGrabacionesTerminadas() {
  const leer = () => terminadas;
  return useSyncExternalStore(gestorGrabacion.suscribir, leer, leer);
}
